import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Receipt, Home, KanbanSquare, PieChart, CalendarClock, Zap, BatteryCharging } from 'lucide-react';
import { supabase } from '../../src/lib/supabase';
import { useAuth } from '../../src/contexts/AuthContext';
import { alcancaCrm, alcancaFinanceiro, alcancaGestao, alcancaSaldos, destinosFinanceiro, rotaNegociacaoCrm, rotaNegociacaoGestao, rotaSaldosUc } from './ucDestinos.ts';

// #6587: a UC citada na solicitação foi clicada. Trava a tela até a pessoa escolher
// o destino ou cancelar; o destino abre na mesma tela, já filtrado pela UC.

interface UcLinha { id: string; numero_uc: string; outro_numero_uc: string | null; nome_cliente: string | null; situacao: string | null; negociacao_id: number | null }
interface NegLinha { id: number; titulo: string | null; status: string | null }

// shrink-0: com o rótulo longo (título da negociação) o ícone não pode ser espremido.
// CRM e Gestão usam os mesmos ícones do menu (Kanban e PieChart); Briefcase é de Projetos.
const ICONES: Record<string, React.ReactNode> = {
    faturas: <Receipt size={16} className="shrink-0" />, cadastro: <Home size={16} className="shrink-0" />, parcelamentos: <CalendarClock size={16} className="shrink-0" />,
};

const UcDestinosModal: React.FC<{ numero: string; onCancelar: () => void; onIr: (rota: string) => void; avisoRascunho?: boolean }> = ({ numero, onCancelar, onIr, avisoRascunho }) => {
    const { profile } = useAuth();
    const dialogoRef = useRef<HTMLDivElement>(null);
    const [uc, setUc] = useState<UcLinha | null>(null);
    const [negs, setNegs] = useState<NegLinha[]>([]);
    const [carregando, setCarregando] = useState(true);
    const [falhou, setFalhou] = useState(false);

    useEffect(() => {
        let vivo = true;
        (async () => {
            const { data: ucs, error: erroUc } = await supabase.from('unidades_consumidoras')
                .select('id, numero_uc, outro_numero_uc, nome_cliente, situacao, negociacao_id')
                .or(`numero_uc_norm.eq.${numero},outro_numero_uc_norm.eq.${numero}`).limit(10);
            const linhas = (ucs ?? []) as UcLinha[];
            // Negociação ligada à UC e, como 42% das UCs não têm o vínculo, também a que
            // tem o número da UC anotado.
            const ids = [...new Set(linhas.map(l => l.negociacao_id).filter((v): v is number => v != null))];
            const numeros = [...new Set(linhas.flatMap(l => [l.numero_uc, l.outro_numero_uc]).filter((v): v is string => !!v))];
            const filtros = [ids.length ? `id.in.(${ids.join(',')})` : '', numeros.length ? `numero_uc.in.(${numeros.map(n => `"${n}"`).join(',')})` : ''].filter(Boolean);
            if (erroUc) throw erroUc;
            const { data: ns, error: erroNeg } = filtros.length
                ? await supabase.from('negociacoes').select('id, titulo, status').or(filtros.join(',')).order('id', { ascending: false }).limit(10)
                : { data: [], error: null };
            // Sem isso, falha de rede apareceria como "sem negociação".
            if (erroNeg) throw erroNeg;
            if (!vivo) return;
            setUc(linhas[0] ?? null);
            setNegs((ns ?? []) as NegLinha[]);
            setCarregando(false);
        })().catch(() => { if (vivo) { setFalhou(true); setCarregando(false); } });
        return () => { vivo = false; };
    }, [numero]);

    useEffect(() => {
        const teclas = (e: KeyboardEvent) => {
            // Esc fecha só esta janela: parar aqui, na captura, impede que chegue em quem está embaixo.
            if (e.key === 'Escape') { e.stopPropagation(); onCancelar(); return; }
            if (e.key !== 'Tab') return;
            // A janela trava a tela: o Tab dá a volta dentro dela, não sai para a solicitação embaixo.
            const itens = Array.from(dialogoRef.current?.querySelectorAll<HTMLElement>('button:not([disabled])') ?? []);
            if (itens.length === 0) return;
            const atual = document.activeElement;
            const dentro = !!atual && !!dialogoRef.current?.contains(atual);
            if (e.shiftKey && (!dentro || atual === itens[0])) { e.preventDefault(); itens[itens.length - 1].focus(); }
            else if (!e.shiftKey && (!dentro || atual === itens[itens.length - 1])) { e.preventDefault(); itens[0].focus(); }
        };
        window.addEventListener('keydown', teclas, true);
        return () => window.removeEventListener('keydown', teclas, true);
    }, [onCancelar]);

    const fin = uc && alcancaFinanceiro(profile) ? destinosFinanceiro(uc.numero_uc) : [];
    const verCrm = alcancaCrm(profile);
    const verGestao = alcancaGestao(profile);
    const verSaldos = alcancaSaldos(profile);

    // Foco por teclado em anel interno (ring-inset): a lista rola e o anel de fora seria cortado.
    const botao = 'w-full flex items-center gap-3 px-3 py-2.5 rounded-lg border text-left text-sm transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary/50 dark:focus-visible:ring-primary-light/60';
    const ativo = `${botao} border-gray-200 dark:border-slate-700 text-gray-800 dark:text-gray-100 hover:border-primary hover:bg-primary/5 dark:hover:border-primary-light dark:hover:bg-primary-light/10`;
    const apagado = `${botao} border-dashed border-gray-200 dark:border-slate-700 text-gray-400 dark:text-gray-500 cursor-not-allowed`;

    const negociacoes = (onde: 'CRM' | 'Gestão', icone: React.ReactNode, rota: (id: number) => string) =>
        negs.length ? negs.map(n => {
            const rotulo = `Negociação #${n.id} ${onde === 'CRM' ? 'no' : 'na'} ${onde}${n.titulo ? ` · ${n.titulo}` : ''}`;
            return (
                <button key={`${onde}-${n.id}`} type="button" className={ativo} title={rotulo} onClick={() => onIr(rota(n.id))}>
                    {icone}<span className="flex-1 min-w-0 truncate">{rotulo}</span>
                </button>
            );
        }) : (
            <button type="button" disabled className={apagado} title="Nenhuma negociação ligada a esta UC">
                {icone}<span className="flex-1 min-w-0">Negociação {onde === 'CRM' ? 'no' : 'na'} {onde}</span>
                <span className="shrink-0 whitespace-nowrap text-xs text-gray-500 dark:text-gray-400">sem negociação</span>
            </button>
        );

    const semUc = !carregando && !falhou && !uc;
    // Defesa: quem não alcança nenhuma tela nem chega aqui (a UC não fica clicável).
    const nada = !!uc && fin.length === 0 && !verCrm && !verGestao && !verSaldos;
    // Só há botão apagado (ex.: vendedor do Comercial, que só alcança o CRM e a UC não tem negociação).
    const semDestino = !!uc && !nada && fin.length === 0 && negs.length === 0 && !verSaldos;
    const destinos = (uc ? fin.length : 0) + (uc && verCrm ? negs.length : 0) + (uc && verGestao ? negs.length : 0) + (uc && verSaldos ? 1 : 0);
    // Região viva única: a lista chega depois de o foco já estar em Cancelar, e quem usa leitor de tela não saberia.
    const anuncio = carregando ? 'Carregando os destinos da UC.'
        : falhou ? ''
        : !uc ? 'UC não encontrada no cadastro.'
        : destinos === 0 ? 'Nenhum destino disponível para esta UC.'
        : `${uc.nome_cliente || 'Cliente sem nome'}. ${destinos} ${destinos === 1 ? 'destino disponível' : 'destinos disponíveis'}.`;

    return createPortal(
        // Clique fora não fecha (só Cancelar ou Esc), e não chega na janela da solicitação embaixo.
        <div className="fixed inset-0 z-[3100] bg-black/50 flex items-center justify-center p-4" onClick={e => e.stopPropagation()}>
            {/* max-h-full + lista rolável: uma UC com várias negociações gera botões demais para a tela. */}
            <div ref={dialogoRef} role="dialog" aria-modal="true" aria-labelledby="uc-destinos-titulo" aria-describedby={avisoRascunho ? 'uc-destinos-aviso' : undefined}
                className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl w-full max-w-md p-6 max-h-full flex flex-col">
                <p role="status" aria-live="polite" className="sr-only">{anuncio}</p>
                <h3 id="uc-destinos-titulo" className="text-base font-bold text-gray-900 dark:text-white flex items-center gap-2">
                    <Zap size={18} className="shrink-0 text-primary dark:text-primary-light" /> <span className="min-w-0 break-words">Para onde ir com a UC {uc?.numero_uc ?? numero}?</span>
                </h3>
                {carregando ? (
                    <div className="mt-2 h-5 w-48 rounded bg-gray-100 dark:bg-slate-800 animate-pulse" aria-hidden="true" />
                ) : uc ? (
                    <p className="text-sm text-gray-600 dark:text-gray-300 mt-1 break-words">
                        {uc.nome_cliente || 'Cliente sem nome'}{uc.situacao ? <> · <span className="font-medium">{uc.situacao}</span></> : null}
                    </p>
                ) : falhou ? null : (
                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">UC não encontrada no cadastro.</p>
                )}
                {/* Texto escrito e ainda não enviado some quando a solicitação fecha para abrir o destino. */}
                {avisoRascunho ? (
                    <p id="uc-destinos-aviso" className="mt-3 text-xs rounded-lg border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20 text-amber-800 dark:text-amber-200 px-3 py-2">
                        Você tem texto não enviado nesta solicitação. Ele será perdido se abrir um destino. Cancele para terminar antes.
                    </p>
                ) : null}
                <div className="mt-4 space-y-2 flex-1 min-h-0 overflow-y-auto">
                    {carregando ? (
                        // Do tamanho dos botões, para a janela não pular quando os destinos chegam.
                        <div aria-hidden="true" className="space-y-2">
                            {[0, 1, 2, 3, 4, 5].map(i => <div key={i} className="h-[42px] rounded-lg bg-gray-100 dark:bg-slate-800 animate-pulse" />)}
                        </div>
                    ) : falhou ? (
                        <p role="alert" className="text-sm text-red-600 dark:text-red-400">Não foi possível carregar os dados desta UC. Feche e tente de novo.</p>
                    ) : semUc ? null : nada ? (
                        <p className="text-sm text-gray-500 dark:text-gray-400">Você não tem acesso às telas desta UC.</p>
                    ) : (
                        <>
                            {fin.filter(d => d.chave !== 'parcelamentos').map(d => (
                                <button key={d.chave} type="button" className={ativo} onClick={() => onIr(d.rota)}>{ICONES[d.chave]}<span className="flex-1">{d.rotulo}</span></button>
                            ))}
                            {verCrm ? negociacoes('CRM', <KanbanSquare size={16} className="shrink-0" />, rotaNegociacaoCrm) : null}
                            {verGestao ? negociacoes('Gestão', <PieChart size={16} className="shrink-0" />, rotaNegociacaoGestao) : null}
                            {fin.filter(d => d.chave === 'parcelamentos').map(d => (
                                <button key={d.chave} type="button" className={ativo} onClick={() => onIr(d.rota)}>{ICONES[d.chave]}<span className="flex-1">{d.rotulo}</span></button>
                            ))}
                            {uc && verSaldos ? (
                                <button type="button" className={ativo} onClick={() => onIr(rotaSaldosUc(uc.numero_uc))}>
                                    <BatteryCharging size={16} className="shrink-0" /><span className="flex-1">Saldo da UC no rateio</span>
                                </button>
                            ) : null}
                            {semDestino ? (
                                <p className="pt-1 text-xs text-gray-500 dark:text-gray-400">Nenhuma negociação desta UC está disponível para você, e você não tem acesso às outras telas dela.</p>
                            ) : null}
                        </>
                    )}
                </div>
                <div className="mt-5 flex justify-end shrink-0">
                    <button type="button" autoFocus onClick={onCancelar}
                        className="px-4 py-2 rounded-lg text-sm font-medium text-gray-700 dark:text-gray-200 border border-gray-200 dark:border-slate-700 hover:bg-gray-50 dark:hover:bg-slate-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/50 dark:focus-visible:ring-primary-light/60">
                        Cancelar
                    </button>
                </div>
            </div>
        </div>,
        document.body,
    );
};

export default UcDestinosModal;
